import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { businesses } from '@/mocks/businesses';
import { supabase } from '@/lib/supabase';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import RoomsSection from './components/RoomsSection';

function BusinessProfile() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { copied, copy } = useCopyToClipboard();

  const business = businesses.find((b) => b.slug === slug);

  const [realBusinessId, setRealBusinessId] = useState<string | null>(null);
  const [isRealHotel, setIsRealHotel] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!slug) {
        if (mounted) setLoadingRooms(false);
        return;
      }
      const { data: biz } = await supabase
        .from('businesses')
        .select('id, category_id')
        .eq('slug', slug)
        .maybeSingle();
      if (!mounted) return;
      if (biz) {
        setRealBusinessId(biz.id);
        if (biz.category_id) {
          const { data: cat } = await supabase
            .from('categories')
            .select('name, slug')
            .eq('id', biz.category_id)
            .maybeSingle();
          if (mounted && cat) {
            const key = `${cat.slug || ''} ${cat.name || ''}`.toLowerCase();
            setIsRealHotel(key.includes('hotel') || key.includes('hôtel'));
          }
        }
      }
      if (mounted) setLoadingRooms(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const isMockHotel = business?.categoryName === 'Hôtels';
  const showRooms = isMockHotel || isRealHotel;
  const roomsBusinessId =
    isRealHotel && realBusinessId ? realBusinessId : business?.id ?? '';

  if (!business) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-16 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-200/70">
            <i className="ri-store-3-line text-2xl text-foreground-400" />
          </div>
          <h1 className="font-heading font-bold text-xl text-foreground-50 mb-2">
            Entreprise non trouvée
          </h1>
          <p className="text-sm text-foreground-400 mb-6">
            Cette entreprise n&apos;existe pas ou a été supprimée.
          </p>
          <button
            onClick={() => navigate('/directory')}
            className="px-6 py-2.5 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors text-sm"
          >
            Retour à l&apos;annuaire
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <i
            key={i}
            className={`${
              i < full ? 'ri-star-fill text-amber-400' : i === full && half ? 'ri-star-half-fill text-amber-400' : 'ri-star-line text-foreground-500'
            } text-sm`}
          />
        ))}
      </div>
    );
  };

  const mapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(business.address + ', ' + business.city + ', ' + business.country)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  const socialIcons: Record<string, string> = {
    facebook: 'ri-facebook-line',
    instagram: 'ri-instagram-line',
    twitter: 'ri-twitter-x-line',
    linkedin: 'ri-linkedin-line',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Cover */}
      <div className="relative h-48 md:h-72 pt-20">
        <img
          src={business.coverUrl}
          alt={business.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
      </div>

      <div className="w-full px-4 md:px-8 lg:px-12 -mt-16 md:-mt-20 relative z-10 pb-16">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1">
            {/* Header Card */}
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 mb-6">
              <div className="flex items-start gap-4 mb-4">
                <img
                  src={business.logoUrl}
                  alt={business.name}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-xl object-cover shrink-0 border border-background-200/70"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-50">
                      {business.name}
                    </h1>
                    {business.isVerified && (
                      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500/10 text-primary-400 text-xs font-semibold">
                        <i className="ri-verified-badge-line" />
                        Vérifié
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground-400 mb-2">
                    {business.categoryName} &middot; {business.city}, {business.country}
                  </p>
                  <div className="flex items-center gap-2">
                    {renderStars(business.rating)}
                    <span className="text-sm text-foreground-400">
                      {business.rating} &middot; {business.reviewCount} avis
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-foreground-400 leading-relaxed mb-5">
                {business.description}
              </p>

              <div className="flex flex-wrap gap-3">
                {business.phone && (
                  <a
                    href={`tel:${business.phone}`}
                    className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-foreground-950 rounded-lg text-sm font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-phone-line" />
                    Appeler
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => copy(window.location.href)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-background-300/60 text-foreground-50 rounded-lg text-sm font-medium hover:bg-foreground-50/5 transition-colors whitespace-nowrap"
                >
                  <i className={copied ? 'ri-check-line text-accent-400' : 'ri-link'} />
                  {copied ? 'Lien copié !' : 'Copier le lien'}
                </button>
                {business.email && (
                  <a
                    href={`mailto:${business.email}`}
                    className="flex items-center gap-2 px-4 py-2.5 border border-background-300/60 text-foreground-50 rounded-lg text-sm font-medium hover:bg-foreground-50/5 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-mail-line" />
                    Email
                  </a>
                )}
                {business.website && (
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 border border-background-300/60 text-foreground-50 rounded-lg text-sm font-medium hover:bg-foreground-50/5 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-global-line" />
                    Site web
                  </a>
                )}
              </div>
            </div>

            {/* Pub */}
            <AdsBanner pageKey="directory" />

            {/* Rooms & Booking (hôtels uniquement) */}
            {showRooms && !loadingRooms && (
              <RoomsSection businessId={roomsBusinessId} isReal={isRealHotel} />
            )}

            {/* Opening Hours */}
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 mb-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                <i className="ri-time-line text-primary-400" />
                Horaires d&apos;ouverture
              </h2>
              <div className="space-y-3">
                {business.openingHours?.map((oh, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-background-200/50 last:border-0">
                    <span className="text-sm text-foreground-400">{oh.day}</span>
                    <span className="text-sm font-medium text-foreground-50">{oh.hours}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Address & Map */}
            <div className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 mb-6">
              <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-400" />
                Adresse
              </h2>
              <p className="text-sm text-foreground-400 mb-4">
                {business.address}, {business.city}, {business.country}
              </p>
              <div className="rounded-lg overflow-hidden border border-background-200/70 h-56 md:h-72">
                <iframe
                  src={mapUrl}
                  title={`Carte - ${business.name}`}
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="w-full lg:w-80 shrink-0 space-y-5">
            {/* Contact Info */}
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="font-heading font-semibold text-base text-foreground-50 mb-4">
                Informations de contact
              </h3>
              <div className="space-y-3">
                {business.phone && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-phone-line text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">Téléphone</p>
                      <p className="text-sm text-foreground-50 truncate">{business.phone}</p>
                    </div>
                  </div>
                )}
                {business.email && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-mail-line text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">Email</p>
                      <p className="text-sm text-foreground-50 truncate">{business.email}</p>
                    </div>
                  </div>
                )}
                {business.website && (
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-global-line text-primary-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-400">Site web</p>
                      <p className="text-sm text-foreground-50 truncate">{business.website.replace('https://', '')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Social */}
            {business.socialLinks && Object.keys(business.socialLinks).length > 0 && (
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <h3 className="font-heading font-semibold text-base text-foreground-50 mb-4">
                  Réseaux sociaux
                </h3>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(business.socialLinks).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200/70 text-foreground-400 hover:text-primary-400 hover:bg-background-300/70 transition-colors"
                      aria-label={platform}
                    >
                      <i className={`${socialIcons[platform] || 'ri-link'} text-lg`} />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Business ID */}
            <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
              <h3 className="font-heading font-semibold text-base text-foreground-50 mb-2">
                Identifiant entreprise
              </h3>
              <p className="text-xs text-foreground-400 mb-1">
                ID unique sur WorldApeiron
              </p>
              <p className="text-sm font-mono text-primary-400 bg-background-50 px-3 py-2 rounded-lg border border-background-200/70">
                WA-BIZ-{business.id.padStart(6, '0')}
              </p>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default BusinessProfile;